import { useEffect, useRef } from 'react';

import { t } from '@ping/helpers';
import { useCountdown } from '@ping/hooks';
import { selectIsNotificationsConnected, useSocketsStore } from '@ping/stores/sockets.store';
import { Button, Spinner } from '@ping/uikit';
import { DateTime, getDuoNumber, isNil } from '@ping/utils';
import { QRCodeSVG } from 'qrcode.react';
import { AssetContractNotificationStatus, NotificationEvents, useNotificationWS } from '@ping/websockets';

import style from './style.module.scss';

const QR_CODE_EXPIRATION_MINUTES = 5;

interface IContractSigningQrCodeProps {
  signingLink: string;
  assetId: string;
  onSigned: () => void;
  onExpired?: () => void;
}

export const ContractSigningQrCode = ({ signingLink, onSigned, onExpired }: IContractSigningQrCodeProps) => {
  const expirationTime = useRef(DateTime.now() + DateTime.minutes(QR_CODE_EXPIRATION_MINUTES));
  const timer = useCountdown(expirationTime.current);
  const isExpired = timer.minutes <= 0 && timer.seconds <= 0;
  const isWsConnected = useSocketsStore(selectIsNotificationsConnected);

  const { notificationData, notificationCallBack } = useNotificationWS();

  notificationCallBack?.(NotificationEvents.OnAssetContractSignatureStatusChanged);

  useEffect(() => {
    if ((notificationData as any)?.status === AssetContractNotificationStatus.Success) {
      onSigned();
    }
  }, [notificationData]);

  useEffect(() => {
    if (isExpired) {
      onExpired?.();
    }
  }, [isExpired]);

  const isTimerValid = !isNaN(timer.minutes) && !isNaN(timer.seconds);

  return (
    <div className={style['contract-signing-qr']}>
      {!isWsConnected && (
        <div className={style['contract-signing-qr__connection-warning']}>
          <Spinner size={2} />
          <span>{t('Connecting...')}</span>
        </div>
      )}

      <div className={style['contract-signing-qr__timer']}>
        <strong className={style['contract-signing-qr__timer-title']}>{t('Expires in')}</strong>
        {isTimerValid && (
          <output className={style['contract-signing-qr__timer-time']}>
            {getDuoNumber(timer.minutes)}:{getDuoNumber(timer.seconds)}
          </output>
        )}
        {!isTimerValid && <Spinner className={style['contract-signing-qr__spinner']} size={4} />}
      </div>

      <div className={style['contract-signing-qr__contrast']} aria-busy={isNil(signingLink)}>
        <QRCodeSVG
          className={style['contract-signing-qr__qr-code']}
          aria-hidden={isNil(signingLink)}
          value={decodeURIComponent(signingLink)}
        />
      </div>

      <Button className={style['contract-signing-qr__open-corepass']} size='large'>
        <a href={signingLink}>{t('Open CorePass')}</a>
      </Button>
    </div>
  );
};
